import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Box,
  Grid,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import Add from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Link as RouterLink } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganizationId } from '../utils/organizationContext';

// Utility function to format dates properly without timezone issues
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Use a UTC time approach with fixed noon time to avoid timezone issues
  return format(new Date(`${dateString.split('T')[0]}T12:00:00Z`), 'dd/MM/yyyy');
};

const NewConvertDashboard = () => {
  const [converts, setConverts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedConvert, setSelectedConvert] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [totalStats, setTotalStats] = useState({
    total: 0,
    pending: 0,
    completed: 0
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { organizationSlug: urlSlug } = useParams();
  const { user, organizationSlug } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState(null);

  // Load organization ID from user or URL slug
  useEffect(() => {
    const loadOrgId = async () => {
      // If user has organization_id, use it directly
      if (user?.organization_id) {
        setCurrentOrgId(user.organization_id);
        return;
      }
      
      // If user is super-admin and we have organization slug in URL, get org ID
      const slug = urlSlug || organizationSlug;
      if (slug && user?.role === 'system_admin' && !user?.organization_id) {
        try {
          const orgId = await getOrganizationId(slug);
          if (orgId) {
            setCurrentOrgId(orgId);
          }
        } catch (err) {
          console.error('Error fetching organization ID:', err);
        }
      }
    };
    loadOrgId();
  }, [urlSlug, organizationSlug, user]);

  const fetchConverts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Format dates to YYYY-MM-DD for the backend with timezone adjustment
      const formatDate = (date) => {
        if (!date) return null;
        // Create a date string that preserves the local date regardless of timezone
        return format(date, 'yyyy-MM-dd');
      };

      const searchParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(dateRange[0] && { startDate: formatDate(dateRange[0]) }),
        ...(dateRange[1] && { endDate: formatDate(dateRange[1]) })
      });

      // For super-admins viewing organization-specific dashboard, add organization_id
      // For regular organization admins, the backend filters by their organization_id automatically
      if (currentOrgId && (user?.role === 'system_admin' || user?.organization_id === currentOrgId)) {
        searchParams.append('organization_id', currentOrgId);
      }

      // Fetch new converts with follow-up stats
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/all?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setConverts(response.data.data || []);
      setTotalCount(response.data.total || 0);
      setTotalStats({
        total: response.data.total || 0,
        pending: response.data.totalPending || 0,
        completed: response.data.totalCompleted || 0
      });
      setLoading(false);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error fetching new converts:', err);
      const errorMessage = err.response?.data?.error || 'Failed to fetch new converts';
      setError(errorMessage);
      setConverts([]);
      setTotalCount(0);
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, dateRange, user, currentOrgId]);

  useEffect(() => {
    if (currentOrgId || user?.organization_id) {
      fetchConverts();
    }
  }, [fetchConverts, currentOrgId, user?.organization_id]);

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/new-converts/${selectedConvert.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      fetchConverts(); // Refresh the list after deletion
      setDeleteDialogOpen(false);
      setSelectedConvert(null);
    } catch (err) {
      setError('Failed to delete new convert');
    }
  };

  const handleViewDetails = (convert) => {
    const slug = urlSlug || organizationSlug;
    const path = slug 
      ? `/${slug}/new-converts/${convert.id}`
      : `/new-converts/${convert.id}`;
    navigate(path);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleStartDateChange = (newValue) => {
    setDateRange([newValue, dateRange[1]]);
    setPage(0);
  };

  const handleEndDateChange = (newValue) => {
    setDateRange([dateRange[0], newValue]);
    setPage(0);
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setSearchQuery('');
    setPage(0);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError('');

      // Format dates to YYYY-MM-DD for the backend
      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().split('T')[0];
      };

      const searchParams = new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(dateRange[0] && { startDate: formatDate(dateRange[0]) }),
        ...(dateRange[1] && { endDate: formatDate(dateRange[1]) })
      });

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('Exporting new converts with params:', searchParams.toString());

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/new-converts/export?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      console.log('Export response:', response);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'wcito-new-converts.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        // Fallback: create filename with current timestamp
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').split('T')[0] + '-' + 
                         now.toTimeString().split(' ')[0].replace(/:/g, '-');
        filename = `wcito-new-converts-${timestamp}.csv`;
      }
      
      console.log('Downloading file:', filename);
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Export completed successfully');

    } catch (error) {
      console.error('Export error:', error);
      console.error('Error response:', error.response);
      setExportError(error.response?.data?.error || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      mt: 4, 
      mb: 4,
      background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
      minHeight: '100vh',
      py: 2
    }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            New Converts Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Manage and track new converts' follow-up status
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
            sx={{
              borderColor: '#DC2626',
              color: '#DC2626',
              '&:hover': {
                borderColor: '#B91C1C',
                backgroundColor: '#fef2f2',
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            component={RouterLink}
            to={organizationSlug ? `/${organizationSlug}/new-convert` : "/new-convert"}
            sx={{
              background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)',
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)'
            }}
          >
            Add New Convert
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(220, 38, 38, 0.4)'
            }
          }}>
            <GroupIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Total New Converts</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(245, 158, 11, 0.4)'
            }
          }}>
            <WarningIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Pending Follow-ups</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.pending}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(16, 185, 129, 0.4)'
            }
          }}>
            <CheckCircleIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Completed</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.completed}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(59, 130, 246, 0.4)'
            }
          }}>
            <PersonAddIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Requires Counselling</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
              {converts.filter(c => c.requires_counselling).length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ 
        p: 3, 
        mb: 3,
        border: '1px solid #fecaca',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 5%, #ffffff 100%)'
      }}>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}
          <Grid container spacing={2} alignItems="center" direction={isMobile ? "column" : "row"}>
            <Grid item xs={12} md={6}>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" width="100%">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={dateRange[0]}
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: isMobile
                      }
                    }}
                    sx={isMobile ? { width: '100%', mb: 1 } : {}}
                  />
                  <DatePicker
                    label="End Date"
                    value={dateRange[1]}
                    onChange={handleEndDateChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: isMobile
                      }
                    }}
                    sx={isMobile ? { width: '100%', mb: 1 } : {}}
                  />
                </LocalizationProvider>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search by name, phone, or email..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                fullWidth={isMobile}
                sx={{
                  borderColor: '#DC2626',
                  color: '#DC2626',
                  '&:hover': {
                    borderColor: '#B91C1C',
                    backgroundColor: '#fef2f2',
                  },
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isMobile ? (
              // Mobile card view
              <Box sx={{ mt: 2 }}>
                <Stack spacing={2}>
                  {converts.length === 0 ? (
                    <Alert severity="info">No new converts found</Alert>
                  ) : (
                    converts.map((convert) => (
                      <Card key={convert.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6">
                              {convert.first_name} {convert.last_name}
                            </Typography>
                            {convert.is_new_convert ? (
                              <Chip label="New Convert" color="success" size="small" />
                            ) : (
                              <Chip label="Regular" color="primary" size="small" />
                            )}
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Date:</strong> {format(new Date(convert.date), 'dd/MM/yyyy')}
                          </Typography>
                          
                          <Typography variant="body2" gutterBottom>
                            <strong>Phone:</strong> {convert.phone_number || 'N/A'}
                          </Typography>
                          
                          <Typography variant="body2" gutterBottom>
                            <strong>Email:</strong> {convert.email || 'N/A'}
                          </Typography>
                          
                          {convert.prayer_request && (
                            <Typography variant="body2" gutterBottom>
                              <strong>Prayer Request:</strong> {convert.prayer_request}
                            </Typography>
                          )}
                          
                          <Typography variant="body2" gutterBottom>
                            <strong>Requires Counselling:</strong> {convert.requires_counselling ? 'Yes' : 'No'}
                          </Typography>
                          
                          {convert.counselling_area && (
                            <Typography variant="body2" gutterBottom>
                              <strong>Counselling Area:</strong> {convert.counselling_area}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDetails(convert)}
                            fullWidth
                          >
                            View Details
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => {
                              setSelectedConvert(convert);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </CardActions>
                      </Card>
                    ))
                  )}
                </Stack>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </Box>
              </Box>
            ) : (
              // Desktop table view
              <>
                <TableContainer>
                  <Table aria-label="new converts table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Prayer Request</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {converts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No new converts found
                          </TableCell>
                        </TableRow>
                      ) : (
                        converts.map((convert) => (
                          <TableRow key={convert.id}>
                            <TableCell>
                              {convert.first_name} {convert.last_name}
                            </TableCell>
                            <TableCell>{format(new Date(convert.date), 'dd/MM/yyyy')}</TableCell>
                            <TableCell>{convert.phone_number || 'N/A'}</TableCell>
                            <TableCell>{convert.email || 'N/A'}</TableCell>
                            <TableCell>{convert.prayer_request || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewDetails(convert)}
                                size="small"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setSelectedConvert(convert);
                                  setDeleteDialogOpen(true);
                                }}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </>
        )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedConvert?.first_name} {selectedConvert?.last_name}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NewConvertDashboard; 